"use client"
import {
  SidebarGroup,
  SidebarGroupLabel,
  SidebarMenu,
  SidebarMenuAction,
  SidebarMenuButton,
  SidebarMenuItem,
  SidebarMenuSub,
  SidebarMenuSubButton,
  SidebarMenuSubItem,
  useSidebar,
} from "@repo/web-ui/components/base/sidebar";
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from "@repo/web-ui/components/base/collapsible";
import {
  Check,
  Cone,
  Copy,
  Delete,
  Edit,
  Hand,
  Minus,
  MoreHorizontal,
  Pencil,
  PersonStanding,
  Plus,
  Rocket,
  Settings2,
  Trash,
  Trash2,
} from "lucide-react";
import { usePathname, useRouter } from "next/navigation";
import { getLocalStoreItem, setLocalStoreItem, useLocalStore } from "../../utils/localStore";
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from "@repo/web-ui/components/base/dropdown-menu";
import { Button } from "@repo/web-ui/components/base/button";
import { Dispatch, Fragment, RefObject, SetStateAction, useEffect, useRef, useState } from "react";
import { Input } from "@repo/web-ui/components/base/input";
import { Pitch, Pitcher } from "@/utils/types";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger } from "@repo/web-ui/components/base/alert-dialog";
import { AlertDescription } from "@repo/web-ui/components/base/alert";
import { Separator } from "@repo/web-ui/components/base/separator";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@repo/web-ui/components/base/select";
import * as THREE from 'three'
import { v4 as uuidv4 } from 'uuid'

interface Props {
  handleNavigationClick: (href: string) => void
}
const NavPitchDesign = ({ handleNavigationClick }:Props) => {
  const pathname = usePathname()
  const { state } = useSidebar()
  const { values, setValues } = useLocalStore()
  const { isMobile } = useSidebar()
  const [renamePitchId, setRenamePitchId] = useState<string>()
  const renameInputRef = useRef<HTMLInputElement>(null)
  const [deleteDialogOpenId, setDeleteDialogueOpenId] = useState<string>()
  const [changePitcherDialogOpenId, setChangePitcherDialogOpenId] = useState<string>()

  useEffect(() => {
    if (renamePitchId && renameInputRef.current) {
      renameInputRef.current.focus()
    }
  },[renamePitchId])

  const handleEditPitcher = (pitcherId: string) => {
    handleNavigationClick(`/pitchers/${pitcherId}`)
  }
  const handleDeletePitcher = (pitcherId: string) => {
    if (setValues) {
      setValues(prev => ({...prev, PITCHERS: prev.PITCHERS.filter(pitcher => pitcher.id !== pitcherId), PITCHES: prev.PITCHES.filter(pitch => pitch.pitcherId !== pitcherId)}))
    }
  }
  const handleRenamePitch = (pitchId: string) => {
    setRenamePitchId(pitchId)
  }
  const handleChangePitchPitcher = (pitchId: string, prevPitcherId: string, targetPitcherId: string) => {
    const pitch = values.PITCHES.find(pitch => pitch.id === pitchId)
    const prevPitcher = values.PITCHERS.find(pitcher => pitcher.id === prevPitcherId)
    const targetPitcher = values.PITCHERS.find(pitcher => pitcher.id === targetPitcherId)
    if (setValues && pitch && prevPitcher && targetPitcher && prevPitcher?.handedness !== targetPitcher?.handedness) {
      const updatedPitch: Pitch = {...pitch, 
        pitcherId: targetPitcherId, 
        scBallOrientation: new THREE.Vector3(-1 * pitch.scBallOrientation.x, pitch.scBallOrientation.y, pitch.scBallOrientation.z),
        ballOrientation: new THREE.Vector3(-1 * pitch.ballOrientation.x, pitch.ballOrientation.y, pitch.ballOrientation.z),
        relativeSpinTiltDeg: -1 * pitch.relativeSpinTiltDeg,
        gyroDeg: -1 * pitch.gyroDeg,
        armTiltDeg: pitch.armTiltDeg ? -1 * pitch.armTiltDeg : undefined
      }
      setValues(prev => ({...prev, PITCHES: prev.PITCHES.map(item => item.id === pitchId ? updatedPitch : item)}))
    } else if (pitch && setValues) {
      const updatedPitch: Pitch = {...pitch, 
        pitcherId: targetPitcherId
      }
      setValues(prev => ({...prev, PITCHES: prev.PITCHES.map(item => item.id === pitchId ? updatedPitch : item)}))
    }
    setChangePitcherDialogOpenId(undefined)
  }
  const handleClonePitch = (pitchId: string) => {
    const pitch = values.PITCHES.find(pitch => pitch.id === pitchId)
    if (setValues && pitch) {
      const newPitch: Pitch = {...pitch,
        id: uuidv4(),
        name: `(Copy) ${pitch.name}`
      }
      setValues(prev => ({...prev, PITCHES: [...prev.PITCHES, newPitch]}))
    }
  }
  const handleDeletePitch = (pitchId: string) => {
    setValues ? setValues(prev => ({...prev, PITCHES: prev.PITCHES.filter(pitch => pitch.id !== pitchId)})) : {}
  }
  const savePitchName = (value: string | undefined, pitchId: string) => {
    const pitch = values.PITCHES.find(pitch => pitch.id === pitchId)
    if (value && value.length > 0 && setValues && pitch) {
      const updatedPitch = {...pitch, name: value, slug: value.toLowerCase().trim().split(" ").join('-')}
      setValues(prev => ({...prev, PITCHES: prev.PITCHES.map(item => item.id === pitchId ? updatedPitch : item)}))
    }
    setRenamePitchId(undefined)
  }
  return (
    <SidebarGroup>
      <SidebarGroupLabel>Pitch Design</SidebarGroupLabel>
      <SidebarMenu>

        {/* Getting Started */}
        {/* TODO(Add back Getting Started menu) */}
        {/* <SidebarMenuItem>
          <SidebarMenuButton 
            tooltip={"Getting started"} 
            data-active={pathname === '/getting-started' ? "true" : "false"}
            onClick={() => handleNavigationClick('/getting-started')}
          >
            <Rocket />
            <span>Getting started</span>
          </SidebarMenuButton>
        </SidebarMenuItem> */}

        {/* My Pitches */}
        <Collapsible asChild 
          className="group/collapsible"
          onOpenChange={(open) => setValues ? setValues(prev => ({...prev, NAV_MENU_STATE: {...prev.NAV_MENU_STATE, ['pitches-expanded']: open}})) : {}}
          defaultOpen={values.NAV_MENU_STATE? values.NAV_MENU_STATE['pitches-expanded'] ?? false : false}
        >
          <SidebarMenuItem >
            <CollapsibleTrigger asChild >
              <SidebarMenuButton tooltip={"Pitches"}
                data-active={state === 'collapsed' && pathname.includes('/pitches/') ? "true" : pathname === '/my-pitches' ? 'true' : "false"}
                onClick={(e) => {
                  if (state === 'collapsed') {
                    handleNavigationClick('/pitches')
                  }
                }}
              >
                <PersonStanding />
                <span>My pitches</span>
                <div className="relative ml-auto h-full">
                  <Plus className="absolute right-0 top-0 bottom-0 size-4 transition group-data-[state=open]/collapsible:opacity-0 group-data-[state=open]/collapsible:rotate-90" />
                  <Minus className="absolute right-0 top-0 bottom-0 size-4 transition group-data-[state=closed]/collapsible:opacity-0 group-data-[state=closed]/collapsible:-rotate-90" />
                </div>
              </SidebarMenuButton>
            </CollapsibleTrigger>
            <CollapsibleContent>

              <SidebarMenuSub >
                {/* Pitchers */}
                {values.PITCHERS.sort((a,b) => a.name.localeCompare(b.name)).map(pitcher => (
                    <Collapsible key={pitcher.id} asChild 
                      className="group/sub-collapsible"
                      onOpenChange={(open) => setValues ? setValues(prev => ({...prev, NAV_MENU_STATE: {...prev.NAV_MENU_STATE, [`pitcher-${pitcher.id}-expanded`]: open}})) : {}}
                      defaultOpen={values.NAV_MENU_STATE? values.NAV_MENU_STATE[`pitcher-${pitcher.id}-expanded`] ?? true : true}
                    >
                      <SidebarMenuSubItem>
                        <div className="flex w-full items-center justify-start gap-2">
                        <CollapsibleTrigger asChild>
                          <SidebarMenuSubButton className="grow">
                            <SidebarGroupLabel>{pitcher.name}</SidebarGroupLabel>
                            <div className="relative ml-auto h-full">
                              <div className="absolute right-0 top-0 bottom-0 flex justify-center items-center">
                                <Plus className="size-4 transition group-data-[state=open]/sub-collapsible:opacity-0 group-data-[state=open]/sub-collapsible:rotate-90" />
                              </div>
                              <div className="absolute right-0 top-0 bottom-0 flex justify-center items-center">
                                <Minus className="size-4 transition group-data-[state=closed]/sub-collapsible:opacity-0 group-data-[state=closed]/sub-collapsible:-rotate-90" />
                              </div>
                            </div>  
                          </SidebarMenuSubButton>
                        </CollapsibleTrigger>
                            <DropdownMenu>
                              <DropdownMenuTrigger asChild>
                                <SidebarMenuAction className="relative top-0 right-0">
                                  <MoreHorizontal />
                                  <span className="sr-only">More</span>
                                </SidebarMenuAction>
                              </DropdownMenuTrigger>
                              <DropdownMenuContent
                                className="w-48 rounded"
                                side={isMobile ? "bottom" : "right"}
                                align={isMobile ? "end" : "start"}
                              >
                                <DropdownMenuItem onClick={() => handleEditPitcher(pitcher.id)}>
                                  <Edit />
                                  <span>Edit pitcher</span>
                                </DropdownMenuItem>
                                <AlertDialog open={deleteDialogOpenId === pitcher.id}>
                                  <AlertDialogTrigger asChild>
                                    <DropdownMenuItem 
                                      variant='destructive' 
                                      className="data-[variant=destructive]:text-destructive data-[variant=destructive]:focus:text-destructive data-[variant=destructive]:*:[svg]:!text-destructive dark:data-[variant=destructive]:text-destructive dark:data-[variant=destructive]:focus:text-destructive-foreground dark:data-[variant=destructive]:focus:*:[svg]:!text-destructive-foreground"
                                      onClick={(e) => {
                                        e.preventDefault()
                                        setDeleteDialogueOpenId(pitcher.id)
                                      }}
                                    >
                                      <Trash2 />
                                      <span>Delete pitcher</span>
                                    </DropdownMenuItem>
                                  </AlertDialogTrigger>
                                  <AlertDialogContent>
                                    <AlertDialogHeader>
                                      <AlertDialogTitle>Are you sure?</AlertDialogTitle>
                                      <AlertDialogDescription>
                                        {`This action cannot be undone. This will permanently delete ${pitcher.name} and all associated pitches.`}
                                      </AlertDialogDescription>
                                    </AlertDialogHeader>
                                    <AlertDialogFooter>
                                      <AlertDialogCancel onClick={() => setDeleteDialogueOpenId(undefined)}>Cancel</AlertDialogCancel>
                                      <AlertDialogAction onClick={() => handleDeletePitcher(pitcher.id)}>{`Delete ${pitcher.name}`}</AlertDialogAction>
                                    </AlertDialogFooter>
                                  </AlertDialogContent>
                                </AlertDialog>
                              </DropdownMenuContent>
                            </DropdownMenu>
                            </div>
                        <CollapsibleContent>
                        <SidebarMenuSub>
                          {/* Pitches */}
                          {values.PITCHES.filter(pitch => pitch.pitcherId === pitcher.id).length === 0 && (
                            <SidebarGroupLabel>- No Pitches -</SidebarGroupLabel>
                          )}
                          {values.PITCHES.sort((a,b) => a.name.localeCompare(b.name)).map(pitch => pitch.pitcherId === pitcher.id && pitch.pitchCategory === 'fastball' ? (
                            <PitchSubMenuItem key={pitch.id} pitcher={pitcher} pitch={pitch} isMobile={isMobile} renameInputRef={renameInputRef} 
                              handleChangePitchPitcher={handleChangePitchPitcher} 
                              handleClonePitch={handleClonePitch}
                              handleDeletePitch={handleDeletePitch}
                              handleNavigationClick={handleNavigationClick}
                              handleRenamePitch={handleRenamePitch}
                              renamePitchId={renamePitchId}
                              savePitchName={savePitchName}
                              deleteDialogOpenId={deleteDialogOpenId}
                              setDeleteDialogueOpenId={setDeleteDialogueOpenId}
                              changePitcherDialogOpenId={changePitcherDialogOpenId}
                              setChangePitcherDialogOpenId={setChangePitcherDialogOpenId}
                            />
                          ) : null)}
                          {values.PITCHES.filter(pitch => pitch.pitcherId === pitcher.id && pitch.pitchCategory === 'breaking-ball').length > 0 && (
                            <Separator />
                          )}
                          {values.PITCHES.sort((a,b) => a.name.localeCompare(b.name)).map(pitch => pitch.pitcherId === pitcher.id && pitch.pitchCategory === 'breaking-ball' ? (
                            <PitchSubMenuItem key={pitch.id} pitcher={pitcher} pitch={pitch} isMobile={isMobile} renameInputRef={renameInputRef} 
                              handleChangePitchPitcher={handleChangePitchPitcher} 
                              handleClonePitch={handleClonePitch}
                              handleDeletePitch={handleDeletePitch}
                              handleNavigationClick={handleNavigationClick}
                              handleRenamePitch={handleRenamePitch}
                              renamePitchId={renamePitchId}
                              savePitchName={savePitchName}
                              deleteDialogOpenId={deleteDialogOpenId}
                              setDeleteDialogueOpenId={setDeleteDialogueOpenId}
                              changePitcherDialogOpenId={changePitcherDialogOpenId}
                              setChangePitcherDialogOpenId={setChangePitcherDialogOpenId}
                            />
                          ) : null)}
                          {values.PITCHES.filter(pitch => pitch.pitcherId === pitcher.id && pitch.pitchCategory === 'offspeed').length > 0 && (
                            <Separator />
                          )}
                          {values.PITCHES.sort((a,b) => a.name.localeCompare(b.name)).map(pitch => pitch.pitcherId === pitcher.id && pitch.pitchCategory === 'offspeed' ? (
                            <PitchSubMenuItem key={pitch.id} pitcher={pitcher} pitch={pitch} isMobile={isMobile} renameInputRef={renameInputRef} 
                              handleChangePitchPitcher={handleChangePitchPitcher} 
                              handleClonePitch={handleClonePitch}
                              handleDeletePitch={handleDeletePitch}
                              handleNavigationClick={handleNavigationClick}
                              handleRenamePitch={handleRenamePitch}
                              renamePitchId={renamePitchId}
                              savePitchName={savePitchName}
                              deleteDialogOpenId={deleteDialogOpenId}
                              setDeleteDialogueOpenId={setDeleteDialogueOpenId}
                              changePitcherDialogOpenId={changePitcherDialogOpenId}
                              setChangePitcherDialogOpenId={setChangePitcherDialogOpenId}
                            />
                          ) : null)}

                          <SidebarMenuSubItem>
                            <SidebarMenuSubButton onClick={() => handleNavigationClick('/pitches/create-pitch?pitcherId=' + pitcher.id)}>
                                <Plus className="opacity-40" />
                                <span className="text-muted-foreground">
                                  New pitch
                                </span>
                            </SidebarMenuSubButton>
                          </SidebarMenuSubItem>
                        </SidebarMenuSub>
                        </CollapsibleContent>
                        
                      </SidebarMenuSubItem>
                    </Collapsible>
                ))}
                  <SidebarMenuSubItem>
                    <SidebarMenuSubButton onClick={() => handleNavigationClick('/pitchers/create-pitcher')}>
                        <Plus className="opacity-40" />
                        <span className="text-muted-foreground">
                          Create new pitcher
                        </span>
                    </SidebarMenuSubButton>
                  </SidebarMenuSubItem>
                </SidebarMenuSub>

            </CollapsibleContent>
          </SidebarMenuItem>
        </Collapsible>

        {/* Visualizer */}
        <SidebarMenuItem>
          <SidebarMenuButton tooltip={"Visualizer"}
            data-active={pathname === '/visualizer' ? "true" : "false"}
            onClick={() => handleNavigationClick('/visualizer')}
          >
            <Cone className="rotate-90" />
            <span>Visualizer</span>
          </SidebarMenuButton>
        </SidebarMenuItem>

        {/* Settings */}
        {/* TODO(Add back settings menu and page if needed) */}
        {/* <SidebarMenuItem>
          <SidebarMenuButton tooltip={"Settings"}
            data-active={pathname === '/settings' ? "true" : "false"}
            onClick={() => handleNavigationClick('/settings')}
          >
            <Settings2 />
            <span>Settings</span>
          </SidebarMenuButton>
        </SidebarMenuItem> */}
      </SidebarMenu>
    </SidebarGroup>
  );
};

interface PitchSubMenuItemProps {
  pitcher: Pitcher
  pitch: Pitch
  renamePitchId: string | undefined
  handleNavigationClick: (route: string) => void
  isMobile: boolean
  handleRenamePitch: (pitchId: string) => void
  handleChangePitchPitcher: (pitchId: string, prevPitcherId: string, targetPitcherId: string) => void
  handleClonePitch: (pitchId: string) => void
  handleDeletePitch: (pitchId: string) => void
  renameInputRef: RefObject<HTMLInputElement | null>
  savePitchName: (value: string | undefined, pitchId: string) => void
  deleteDialogOpenId: string | undefined
  setDeleteDialogueOpenId: Dispatch<SetStateAction<string | undefined>>
  changePitcherDialogOpenId: string | undefined
  setChangePitcherDialogOpenId: Dispatch<SetStateAction<string | undefined>>
}
const PitchSubMenuItem = ({
  pitcher,
  pitch,
  renamePitchId,
  handleNavigationClick,
  isMobile,
  handleRenamePitch,
  handleChangePitchPitcher,
  handleClonePitch,
  handleDeletePitch,
  renameInputRef,
  savePitchName,
  deleteDialogOpenId,
  setDeleteDialogueOpenId,
  changePitcherDialogOpenId,
  setChangePitcherDialogOpenId
}:PitchSubMenuItemProps) => {
  const { values } = useLocalStore()
  const [targetPitcherId, setTargetPitcherId] = useState<string>()

  return (
    <SidebarMenuSubItem key={pitch.id}>
      {renamePitchId !== pitch.id && (
        <div className="flex w-full items-center justify-start gap-2">
        <SidebarMenuSubButton className="grow" onClick={() => handleNavigationClick(`/pitches/${pitch.id}`)}>
            <span>{pitch.name}</span>
        </SidebarMenuSubButton>
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <SidebarMenuAction className="relative top-0 right-0 shrink-0">
              <MoreHorizontal />
              <span className="sr-only">More</span>
            </SidebarMenuAction>
          </DropdownMenuTrigger>
          <DropdownMenuContent
            className="w-48 rounded-lg"
            side={isMobile ? "bottom" : "right"}
            align={isMobile ? "end" : "start"}
          >
            <DropdownMenuItem onClick={() => handleRenamePitch(pitch.id)}>
              <Pencil />
              <span>Rename</span>
            </DropdownMenuItem>
            <AlertDialog open={changePitcherDialogOpenId === pitch.id}>
              <AlertDialogTrigger asChild>
                <DropdownMenuItem onClick={(e) => {
                      e.preventDefault()
                      setChangePitcherDialogOpenId(pitch.id)
                    }}
                >
                  <PersonStanding />
                  <span>Change pitcher</span>
                </DropdownMenuItem>
              </AlertDialogTrigger>
              <AlertDialogContent>
                  <AlertDialogHeader>
                    <AlertDialogTitle>{`Assign ${pitch.name} to a new pitcher`}</AlertDialogTitle>
                    <AlertDialogDescription>
                      {`Select the pitcher you would like to assign ${pitch.name} to.`}
                    </AlertDialogDescription>
                  </AlertDialogHeader>
                  <Select onValueChange={setTargetPitcherId}>
                    <SelectTrigger>
                      <SelectValue placeholder='Select a new pitcher' />
                    </SelectTrigger>
                    <SelectContent>
                      {values.PITCHERS.sort((a,b) => a.name.localeCompare(b.name)).map(pitcher => (
                        <SelectItem key={pitcher.id} value={pitcher.id}>{pitcher.name}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  <AlertDialogFooter>
                    <AlertDialogCancel onClick={() => setChangePitcherDialogOpenId(undefined)}>Cancel</AlertDialogCancel>
                    <AlertDialogAction disabled={!targetPitcherId} onClick={() => handleChangePitchPitcher(pitch.id, pitcher.id, targetPitcherId!)}>{`Save`}</AlertDialogAction>
                  </AlertDialogFooter>
                </AlertDialogContent>
            </AlertDialog>
            <DropdownMenuItem onClick={() => handleClonePitch(pitch.id)}>
              <Copy />
              <span>Clone pitch</span>
            </DropdownMenuItem>
            
              <AlertDialog open={deleteDialogOpenId === pitch.id}>
                <AlertDialogTrigger asChild>
                  <DropdownMenuItem 
                    variant='destructive' 
                    className="data-[variant=destructive]:text-destructive data-[variant=destructive]:focus:text-destructive data-[variant=destructive]:*:[svg]:!text-destructive dark:data-[variant=destructive]:text-destructive dark:data-[variant=destructive]:focus:text-destructive-foreground dark:data-[variant=destructive]:focus:*:[svg]:!text-destructive-foreground"
                    onClick={(e) => {
                      e.preventDefault()
                      setDeleteDialogueOpenId(pitch.id)
                    }}
                  >
                    <Trash2 />
                    <span>Delete pitch</span>
                  </DropdownMenuItem>
                </AlertDialogTrigger>
                <AlertDialogContent>
                  <AlertDialogHeader>
                    <AlertDialogTitle>Are you sure?</AlertDialogTitle>
                    <AlertDialogDescription>
                      {`This action cannot be undone. This will permanently delete ${pitch.name}.`}
                    </AlertDialogDescription>
                  </AlertDialogHeader>
                  <AlertDialogFooter>
                    <AlertDialogCancel onClick={() => setDeleteDialogueOpenId(undefined)}>Cancel</AlertDialogCancel>
                    <AlertDialogAction onClick={() => handleDeletePitch(pitch.id)}>{`Delete ${pitch.name}`}</AlertDialogAction>
                  </AlertDialogFooter>
                </AlertDialogContent>
              </AlertDialog>
          </DropdownMenuContent>
        </DropdownMenu>
        </div>
      )}
      {renamePitchId === pitch.id && (
        <SidebarMenuSubButton className="overflow-visible hover:bg-transparent active:bg-transparent data-[active=true]:bg-transparent">
            <Input ref={renameInputRef} type="text" className="h-fit w-full mr-6" defaultValue={pitch.name} />
            <SidebarMenuAction onClick={() => savePitchName(renameInputRef.current?.value, pitch.id)} >
              <Check />
            </SidebarMenuAction>
        </SidebarMenuSubButton>
      )}
    </SidebarMenuSubItem>
  )
}

export default NavPitchDesign;
